<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faTimes, faCalendar } from '@fortawesome/pro-light-svg-icons';
  import { selectedParticipant } from '$stores/selectionStore';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  // SPTL-492: TZ-aware parsing — interpret <input type="datetime-local">
  // values as wall-clock time in the participant's timezone (legacy parity).
  // Without this, NG was interpreting the input as browser-local time, so a
  // dev in PT entering a date for an ET participant would save the wrong UTC.
  import { fromZonedTime } from 'date-fns-tz';


  // Props
  export let show = false;
  export let subjectActivity = null;
  export let timezone = 'UTC';

  // API configuration
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  $: isAdmin = hasAccessUiAction(
    'schedule.subjectActivityEditor.admin',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  
  // Check if activity is of type 'Measurement'
  $: isMeasurementActivity = subjectActivity?.typeLabel === 'Measurement';
  // Milestone visit (MILE) or Extension (EXT) → save dates as date-only YYYY-MM-DD,
  // not datetime ISO. Without this, NG was saving '2026-08-15T12:00:00Z' for
  // milestones — legacy stores just '2026-08-15'.
  $: isMilestoneActivity =
    subjectActivity?.typeCode === 'MILE' || subjectActivity?.typeCode === 'EXT';

  // Form state
  let planned = null;
  let started = null;
  let completed = null;
  let visitDate = null;
  let statusOptions = [];
  let selectedStatusId = '';
  let cgmBlinded = false;
  let smbgBlinded = false;
  let enableAlerts = false;
  let isLoading = false;
  let errorMessage = '';

  // Validation state
  let validationErrors = {
    planned: '',
    started: '',
    completed: '',
    status: ''
  };

  // Track auto-set dates for user feedback
  let autoSetDates = {
    started: false,
    completed: false
  };

  // Track initialization
  let isInitialized = false;
  let prevSubjectActivityId = null;

  // Store original data for change detection
  let originalActivityData = null;
  let hasChanges = false;

  // Debug counter
  let updateCounter = 0;

  function forceUpdate() {
    updateCounter++;
    console.log('Forced update:', updateCounter);
  }

  // For direct input handling
  function handleStatusChange(event) {
    //console.log('Status change event:', event);
    //console.log('Previous status:', selectedStatusId);
    //console.log('New status:', event.target.value);
    const newStatusId = event.target.value;
    const previousStatusId = selectedStatusId;
    
    selectedStatusId = newStatusId;

    // Find the selected status label for better debugging
    const selectedStatus = statusOptions.find((s) => s.id === selectedStatusId);
    //console.log('Selected status object:', selectedStatus);

    // Clear validation errors when status changes
    validationErrors.status = '';
    
    // Auto-set dates based on status change
    // Status transitions:
    // - Not Started → Started: sets started date
    // - Not Started → Completed: sets both started and completed dates
    // - Started → Completed: sets only completed date (preserves existing started date)
    // - Any → Not Started: clears both dates
    if (selectedStatus) {
      const now = new Date();
      
      if (selectedStatus.code === 'START') {
        // When changing to "Started", set started date to now and clear completed date
        started = now;
        completed = null; // Clear completed date since activity is being started
        validationErrors.started = ''; // Clear any previous validation errors
        validationErrors.completed = ''; // Clear any previous validation errors
        autoSetDates.started = true;
        autoSetDates.completed = false; // Reset auto-set flag for completed
        console.log('Auto-set started date to now and cleared completed date:', now);
      } else if (selectedStatus.code === 'COMP') {
        // When changing to "Completed", set completed date to now
        // If started date is not set, also set it to now (for direct Not Started → Completed transition)
        completed = now;
        validationErrors.completed = ''; // Clear any previous validation errors
        autoSetDates.completed = true;
        
        if (!started) {
          // If no started date exists, set it to now as well
          started = now;
          validationErrors.started = ''; // Clear any previous validation errors
          autoSetDates.started = true;
          console.log('Auto-set both started and completed dates to now:', now);
        } else {
          // If started date already exists, only set completed date
          console.log('Auto-set completed date to now (started date already exists):', now);
        }
      } else if (selectedStatus.code === 'NOTSTART') {
        // When changing to "Not Started", clear started and completed dates
        started = null;
        completed = null;
        validationErrors.started = '';
        validationErrors.completed = '';
        autoSetDates.started = false;
        autoSetDates.completed = false;
        console.log('Cleared started and completed dates for "Not Started" status');
      }
    }
    
    forceUpdate();
  }

  function handleDateTimeChange(dateType, event) {
    const value = event.target.value;
    console.log(`${dateType} date/time changed to:`, value);

    // Parse the datetime value directly
    if (dateType === 'planned') {
      planned = value ? new Date(value) : null;
      validationErrors.planned = ''; // Clear validation error
    } else if (dateType === 'started') {
      // SPTL-492 parity: milestone activities save date-only (parseDateOnly),
      // non-milestone (Measurement) save full datetime in participant TZ.
      started = isMilestoneActivity ? parseDateOnly(value) : parseDateTime(value);
      validationErrors.started = ''; // Clear validation error
      autoSetDates.started = false; // Reset auto-set flag when manually changed
    } else if (dateType === 'completed') {
      completed = isMilestoneActivity ? parseDateOnly(value) : parseDateTime(value);
      validationErrors.completed = ''; // Clear validation error
      autoSetDates.completed = false; // Reset auto-set flag when manually changed
    } else if (dateType === 'visit') {
      // SPTL-492: legacy parses visitDate to a Date object so the save flow's
      // formatDateTimeForUpdate(Date) → .toISOString() produces proper ISO
      // (e.g., "2026-06-27T02:12:00.000Z"). Storing the raw datetime-local
      // string ("2026-06-25T22:12") caused the backend to silently drop it
      // and the field re-displayed empty on reopen.
      visitDate = parseDateTime(value);
    }

    forceUpdate();
  }

  function handleCheckboxChange(field, event) {
    console.log(`${field} checkbox clicked:`, event.target.checked);

    if (field === 'cgmBlinded') {
      cgmBlinded = !cgmBlinded; // Toggle directly
    } else if (field === 'smbgBlinded') {
      smbgBlinded = !smbgBlinded; // Toggle directly
    } else if (field === 'enableAlerts') {
      enableAlerts = !enableAlerts; // Toggle directly
    }

    console.log(
      `${field} changed to:`,
      field === 'cgmBlinded' ? cgmBlinded : field === 'smbgBlinded' ? smbgBlinded : enableAlerts
    );
    forceUpdate();
  }

  const dispatch = createEventDispatcher();

  // Initialize form values when activity changes
  function initializeFormValues() {
    if (!subjectActivity) return;

    console.log('Initializing form values from activity:', subjectActivity);

    // Helper function to properly parse dates to ensure consistency
    function parseDateValue(dateVal) {
      if (!dateVal) return null;
      return new Date(dateVal);
    }

    planned = parseDateValue(subjectActivity.planned);
    started = parseDateValue(subjectActivity.started);
    completed = parseDateValue(subjectActivity.completed);
    
    // For visitDate, keep as a Date object (legacy parity). NG previously
    // pre-formatted to a datetime-local string here, which then got passed
    // back through formatDateTimeForInput() on render and ended up empty
    // after save+reopen. Consistent Date object → predictable formatting.
    if (subjectActivity.visitDate) {
      if (typeof subjectActivity.visitDate === 'string' && !subjectActivity.visitDate.includes('T')) {
        // If it's just a date string, add default time then parse
        visitDate = parseDateTime(subjectActivity.visitDate + 'T12:00');
      } else {
        visitDate = parseDateValue(subjectActivity.visitDate);
      }
    } else {
      visitDate = null;
    }
    
    cgmBlinded = subjectActivity.cgmBlinded ?? false;
    smbgBlinded = subjectActivity.smbgBlinded ?? false;
    enableAlerts = subjectActivity.enableAlerts ?? false;

    // Find the correct status ID based on either statusGUID or statusCode
    if (subjectActivity.statusGUID) {
      selectedStatusId = String(subjectActivity.statusGUID);
      console.log('Setting status from statusGUID:', selectedStatusId);
    } else if (subjectActivity.statusCode && statusOptions.length > 0) {
      // Find the status option with matching code
      const matchingOption = statusOptions.find((opt) => opt.code === subjectActivity.statusCode);
      if (matchingOption) {
        selectedStatusId = String(matchingOption.id);
        console.log(
          'Setting status from statusCode:',
          subjectActivity.statusCode,
          'to ID:',
          selectedStatusId
        );
      } else {
        selectedStatusId = '';
        console.log('Status code not found in options:', subjectActivity.statusCode);
      }
    } else {
      selectedStatusId = '';
      console.log('No status information available');
    }

    console.log('Form initialized with:', {
      planned,
      started,
      completed,
      visitDate,
      selectedStatusId,
      cgmBlinded,
      smbgBlinded,
      enableAlerts,
    });

    // Store original data for change detection
    originalActivityData = {
      planned: planned,
      started: started,
      completed: completed,
      visitDate: visitDate,
      statusGUID: selectedStatusId,
      cgmBlinded: cgmBlinded,
      smbgBlinded: smbgBlinded,
      enableAlerts: enableAlerts
    };
    hasChanges = false;

    // Reset auto-set flags on initialization
    autoSetDates = {
      started: false,
      completed: false
    };

    // Mark as initialized and store ID
    isInitialized = true;
    prevSubjectActivityId = subjectActivity._id;

    forceUpdate();
  }

  // Always reinitialize when the form is shown to ensure fresh data
  // This prevents unsaved changes from persisting when the form is reopened
  $: if (show && subjectActivity) {
    console.log('Form shown, initializing with activity:', subjectActivity._id);
    // If statusOptions are loaded, initialize right away
    if (statusOptions.length > 0) {
      initializeFormValues();
    } else {
      // Otherwise, load options first
      loadStatusOptions().then(() => {
        initializeFormValues();
      });
    }
  }

  // Load options only when needed
  $: if (show && !statusOptions.length) {
    console.log('Loading status options - options empty');
    loadStatusOptions();
  }

  // Load status options from the API
  async function loadStatusOptions() {
    if (isLoading) {
      console.log('Skipping load - already loading');
      return;
    }

    try {
      isLoading = true;
      console.log('Loading status options from API');

      const response = await fetch(`${API_BASE_URL}/lookups/SUBACTSTAT`);

      if (!response.ok) {
        throw new Error('Failed to load status options');
      }

      const responseData = await response.json();

      // Extract the array from the data property
      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        statusOptions = responseData.data;
        console.log('Loaded status options:', statusOptions.length);
        console.log(
          'Options:',
          statusOptions.map((o) => ({ id: o.id, code: o.code, label: o.label }))
        );

        // Don't update the selectedStatusId here since we'll do it in initializeFormValues
        forceUpdate();
      } else {
        console.error('API returned invalid data structure for status options:', responseData);
        statusOptions = [];
        errorMessage = 'Invalid status options data received';
      }
    } catch (error) {
      console.error('Error loading status options:', error);
      errorMessage = 'Failed to load status options';
      statusOptions = []; // Ensure it's an empty array on error
    } finally {
      isLoading = false;
    }

    return statusOptions;
  }

  // Function to format date and time for the datetime-local picker input
  // SPTL-492 parity: legacy uses date-only <input type="date"> for milestone
  // started/completed (and always for planned). Returns 'YYYY-MM-DD' so
  // <input type="date"> displays it correctly. Preserves plain date strings
  // as-is to avoid TZ shifts that occur when parsing "YYYY-MM-DD" as UTC.
  function formatDateForInput(dateVal) {
    if (!dateVal) return '';
    if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
      return dateVal;
    }
    const parsedDate = dateVal instanceof Date ? dateVal : new Date(dateVal);
    if (Number.isNaN(parsedDate.getTime())) return '';
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone || 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(parsedDate);
      const get = (t) => parts.find((p) => p.type === t)?.value;
      const y = get('year'), m = get('month'), d = get('day');
      if (!y || !m || !d) return '';
      return `${y}-${m}-${d}`;
    } catch (e) {
      return parsedDate.toISOString().slice(0, 10);
    }
  }

  function formatDateTimeForInput(date) {
    if (!date) return '';
    if (date instanceof Date) {
      // Format as YYYY-MM-DDTHH:mm for datetime-local input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    // If it's a string, try to parse it as a date
    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return formatDateTimeForInput(parsedDate);
      }
      // If it's just a date string like "2023-10-24", add default time
      if (date.length === 10) {
        return `${date}T12:00`;
      }
    }
    return date;
  }

  // Function to parse date and time from input value.
  // SPTL-492: interprets `value` (a YYYY-MM-DDTHH:mm string from a
  // datetime-local input) as wall-clock time in the participant's tz and
  // returns the corresponding UTC Date. Matches legacy behavior.
  function parseDateTime(value) {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    try {
      return fromZonedTime(value, timezone || 'UTC');
    } catch (error) {
      console.error('Error parsing datetime input:', error);
      const fallback = new Date(value);
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    }
  }

  // Date-only parse: a "YYYY-MM-DD" string from an <input type="date">
  // is treated as midnight (00:00:00) in the participant timezone.
  // Used for milestone started/completed dates (date-only, no time).
  function parseDateOnly(value) {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    try {
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return fromZonedTime(`${value}T00:00:00`, timezone || 'UTC');
      }
      return parseDateTime(value);
    } catch (error) {
      console.error('Error parsing date-only input:', error);
      return null;
    }
  }

  let plannedStr = '';
  let startedStr = '';
  let completedStr = '';

  // Handle changes to string values
  $: if (plannedStr) {
    const newDate = parseDateTime(plannedStr);
    if (!planned || newDate.getTime() !== planned.getTime()) {
      planned = newDate;
    }
  }

  $: if (startedStr) {
    const newDate = parseDateTime(startedStr);
    if (!started || newDate.getTime() !== started.getTime()) {
      started = newDate;
    }
  }

  $: if (completedStr) {
    const newDate = parseDateTime(completedStr);
    if (!completed || newDate.getTime() !== completed.getTime()) {
      completed = newDate;
    }
  }

  function addHours(date, hours) {
    const d = new Date(date);
    d.setHours(d.getHours() + hours);
    return d;
  }

  // Validation functions
  function validateForm() {
    // Reset validation errors
    validationErrors = {
      planned: '',
      started: '',
      completed: '',
      status: ''
    };

    let isValid = true;

    // 5. Planned date must always be set
    if (!planned) {
      validationErrors.planned = 'Planned date is required';
      isValid = false;
    }

    // Get the selected status object
    const selectedStatus = statusOptions.find(s => s.id === selectedStatusId);
    if (!selectedStatus) {
      validationErrors.status = 'Please select a valid status';
      isValid = false;
      return isValid;
    }

    const statusCode = selectedStatus.code;

    // 1. If status is "Started", started date must be set
    if (statusCode === 'START' && !started) {
      validationErrors.started = 'Started date is required when status is "Started"';
      isValid = false;
    }

    // 2. If status is "Completed", completed date must be set
    if (statusCode === 'COMP' && !completed) {
      validationErrors.completed = 'Completed date is required when status is "Completed"';
      isValid = false;
    }

    // 3. Completed date must be after started date
    if (completed && started && completed <= started) {
      validationErrors.completed = 'Completed date must be after started date';
      isValid = false;
    }

    // 4. If status is "Not Started", neither started nor completed dates can be set
    if (statusCode === 'NOTSTART') {
      if (started) {
        validationErrors.started = 'Started date cannot be set when status is "Not Started"';
        isValid = false;
      }
      if (completed) {
        validationErrors.completed = 'Completed date cannot be set when status is "Not Started"';
        isValid = false;
      }
    }

    return isValid;
  }



  // Handle save
  async function handleSave() {
    try {
      if (!subjectActivity) {
        throw new Error('No subject activity to update');
      }

      // Validate form before saving
      if (!validateForm()) {
        errorMessage = 'Please fix the validation errors before saving';
        return;
      }

      isLoading = true;
      errorMessage = '';

      let statusGUID = selectedStatusId;

      console.log('Submitting form with values:', {
        planned,
        started,
        completed,
        visitDate,
        statusGUID,
        cgmBlinded,
        smbgBlinded,
        enableAlerts,
      });

      // Format all dates consistently using ISO strings with full datetime
      function formatDateTimeForUpdate(dateVal) {
        if (!dateVal) return null;
        if (typeof dateVal === 'string') {
          // If it's already a datetime string, return as is
          if (dateVal.includes('T')) return dateVal;
          // If it's just a date string, add default time
          if (dateVal.length === 10) return dateVal + 'T12:00:00Z';
          return dateVal;
        }
        if (dateVal instanceof Date) return dateVal.toISOString();
        return dateVal;
      }

      // SPTL-492 parity: milestone activities save date-only (YYYY-MM-DD),
      // sub-activities save full datetime. Without these helpers, NG was
      // appending 'T12:00:00Z' to milestone dates and breaking parity with
      // legacy. Helpers ported from legacy SubjectActivityEditor lines 476-526.
      function formatPlannedDateForUpdate(dateVal) {
        if (!dateVal) return null;
        // Already a YYYY-MM-DD string
        if (typeof dateVal === 'string' && dateVal.length === 10 && dateVal.includes('-')) {
          return dateVal;
        }
        // Convert Date object or ISO string -> YYYY-MM-DD
        const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
        if (Number.isNaN(d.getTime())) return null;
        return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
          .toISOString().slice(0, 10);
      }
      function formatProgressDateForUpdate(dateVal) {
        if (!dateVal) return null;
        return formatPlannedDateForUpdate(dateVal);
      }

      // For milestone activities: started/completed save as date-only.
      // For non-milestone: save as full datetime (preserves time precision
      // for sub-activities like CGM sensor wear).
      const formatStartedCompletedForUpdate = isMilestoneActivity
        ? formatProgressDateForUpdate
        : formatDateTimeForUpdate;

      // For non-admin users, preserve the original blinded/alerts settings
      const updates = {
        planned: formatPlannedDateForUpdate(planned),
        started: formatStartedCompletedForUpdate(started),
        completed: formatStartedCompletedForUpdate(completed),
        visitDate: formatDateTimeForUpdate(visitDate),
        statusGUID,
      };

      // Only include the checkbox values if the user is an admin
      if (isAdmin) {
        updates.cgmBlinded = cgmBlinded;
        updates.smbgBlinded = smbgBlinded;
        updates.enableAlerts = enableAlerts;
      } else {
        // Preserve original values for non-admin users
        if (subjectActivity.cgmBlinded !== undefined)
          updates.cgmBlinded = subjectActivity.cgmBlinded;
        if (subjectActivity.smbgBlinded !== undefined)
          updates.smbgBlinded = subjectActivity.smbgBlinded;
        if (subjectActivity.enableAlerts !== undefined)
          updates.enableAlerts = subjectActivity.enableAlerts;
      }

      const payload = {
        subjectActivityGUID: subjectActivity._id,
        timezone,
        updates,
      };

      const response = await fetch(`${API_BASE_URL}/subjectSchedule/update-subject-activity`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update subject activity');
      }

      // Close the modal and notify of success
      dispatch('saved', await response.json());
      handleClose();
    } catch (error) {
      console.error('Error saving subject activity:', error);
      errorMessage = error.message || 'An error occurred while saving';
    } finally {
      isLoading = false;
    }
  }

  // Detect changes by comparing current data with original data
  $: if (originalActivityData && isInitialized) {
    const currentData = {
      planned: planned,
      started: started,
      completed: completed,
      visitDate: visitDate,
      statusGUID: selectedStatusId,
      cgmBlinded: cgmBlinded,
      smbgBlinded: smbgBlinded,
      enableAlerts: enableAlerts
    };
    
    hasChanges = JSON.stringify(currentData) !== JSON.stringify(originalActivityData);
  }

  // Handle cancel
  function handleClose() {
    // Reset change detection when closing
    originalActivityData = null;
    hasChanges = false;
    errorMessage = '';
    // Clear validation errors
    validationErrors = {
      planned: '',
      started: '',
      completed: '',
      status: ''
    };
    // Reset auto-set flags
    autoSetDates = {
      started: false,
      completed: false
    };
    // Reset initialization state so form will reinitialize next time
    isInitialized = false;
    prevSubjectActivityId = null;
    dispatch('close');
    show = false;
  }

  // Log when selectedStatusId changes
  // $: console.log('selectedStatusId changed to:', selectedStatusId);

  // Trigger validation when relevant fields change
  $: if (isInitialized && (planned || started || completed || selectedStatusId)) {
    // Only validate if we have the necessary data loaded
    if (statusOptions.length > 0) {
      validateForm();
    }
  }
</script>

{#if show}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
    transition:fade={{ duration: 200 }}
  >
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="modal-overlay absolute inset-0 bg-slate-800 opacity-75"
      on:click={handleClose}
    ></div>

    <div class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-3xl relative z-60 overflow-y-auto max-h-[90vh]">
      <header class="flex justify-between items-start mb-4 border-b pb-2 dark:border-gray-700">
        <div class="flex items-center gap-2">
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">Edit Activity</h2>
          {#if hasChanges}
            <span class="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full dark:bg-yellow-900 dark:text-yellow-200">
              Modified
            </span>
          {/if}
          {#if Object.values(validationErrors).some(error => error)}
            <span class="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full dark:bg-red-900 dark:text-red-200">
              Validation Errors
            </span>
          {/if}
        </div>
        <button
          on:click={handleClose}
          class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        >
          &times;
        </button>
      </header>

      {#if errorMessage}
        <div class="mb-4 p-2 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded">
          {errorMessage}
        </div>
      {/if}

      {#if Object.values(validationErrors).some(error => error)}
        <div class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <h4 class="text-sm font-medium text-red-800 dark:text-red-200 mb-2">Please fix the following validation errors:</h4>
          <ul class="text-sm text-red-700 dark:text-red-300 space-y-1">
            {#each Object.entries(validationErrors) as [field, error]}
              {#if error}
                <li class="flex items-start">
                  <span class="text-red-500 mr-1">•</span>
                  <span>{error}</span>
                </li>
              {/if}
            {/each}
          </ul>
        </div>
      {/if}

      <form on:submit|preventDefault={handleSave} class="space-y-4">
        <div class="grid grid-cols-3 gap-3">
          <div class="form-group">
            <label for="activity-name" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Activity</label
            >
            <input
              type="text"
              id="activity-name"
              value={subjectActivity?.name || ''}
              readonly
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 bg-gray-100"
            />
          </div>

          <div class="form-group">
            <label for="status" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Status</label
            >
            <select
              id="status"
              on:change={handleStatusChange}
              disabled={isLoading}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none {validationErrors.status ? 'border-red-500' : ''}"
            >
              {#each statusOptions as status}
                <option value={status.id} selected={status.id === selectedStatusId}>
                  {status.label}
                </option>
              {/each}
            </select>
            {#if validationErrors.status}
              <div class="text-red-500 text-xs mt-1">{validationErrors.status}</div>
            {/if}
          </div>

          {#if !isMeasurementActivity}
          <div class="form-group">
            <label for="visit-date" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Visit Date</label
            >
            <div class="relative">
              <input
                type="datetime-local"
                id="visit-date"
                value={formatDateTimeForInput(visitDate)}
                on:change={(e) => handleDateTimeChange('visit', e)}
                disabled={isLoading}
                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 bg-white [&::-webkit-calendar-picker-indicator]:hidden"
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-auto">
                <button
                  type="button"
                  on:click={() => document.getElementById('visit-date').showPicker()}
                  class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                >
                  <FontAwesomeIcon icon={faCalendar} />
                </button>
              </div>
            </div>
          </div>
          {/if}
        </div>

        <!-- Date inputs in a row -->
        <div class="grid grid-cols-3 gap-3">
          <div class="form-group">
            <label for="planned-date" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Planned Date</label
            >
            <div class="relative">
              <input
                type="date"
                id="planned-date"
                value={formatDateForInput(planned)}
                on:change={(e) => handleDateTimeChange('planned', e)}
                disabled={isLoading}
                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 bg-white [&::-webkit-calendar-picker-indicator]:hidden {validationErrors.planned ? 'border-red-500' : ''}"
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-auto">
                <button
                  type="button"
                  on:click={() => document.getElementById('planned-date').showPicker()}
                  class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                >
                  <FontAwesomeIcon icon={faCalendar} />
                </button>
              </div>
            </div>
            {#if validationErrors.planned}
              <div class="text-red-500 text-xs mt-1">{validationErrors.planned}</div>
            {/if}
          </div>

          <div class="form-group">
            <label for="started-date" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >{isMilestoneActivity ? 'Started Date' : 'Started Date & Time'}
              {#if autoSetDates.started}
                <span class="text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded ml-1 dark:bg-blue-900 dark:text-blue-200">
                  Auto-set
                </span>
              {/if}
            </label>
            <div class="relative">
              <input
                type={isMilestoneActivity ? 'date' : 'datetime-local'}
                id="started-date"
                value={isMilestoneActivity ? formatDateForInput(started) : formatDateTimeForInput(started)}
                on:change={(e) => handleDateTimeChange('started', e)}
                disabled={isLoading}
                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 bg-white [&::-webkit-calendar-picker-indicator]:hidden {validationErrors.started ? 'border-red-500' : ''}"
              />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-auto">
                <button
                  type="button"
                  on:click={() => document.getElementById('started-date').showPicker()}
                  class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                >
                  <FontAwesomeIcon icon={faCalendar} />
                </button>
              </div>
            </div>
            {#if validationErrors.started}
              <div class="text-red-500 text-xs mt-1">{validationErrors.started}</div>
            {/if}
          </div>

          <div class="form-group">
            <label for="completed-date" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >{isMilestoneActivity ? 'Completed Date' : 'Completed Date & Time'}
              {#if autoSetDates.completed}
                <span class="text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded ml-1 dark:bg-blue-900 dark:text-blue-200">
                  Auto-set
                </span>
              {/if}
            </label>
            <div class="relative">
              <input
                type={isMilestoneActivity ? 'date' : 'datetime-local'}
                id="completed-date"
                value={isMilestoneActivity ? formatDateForInput(completed) : formatDateTimeForInput(completed)}
                on:change={(e) => handleDateTimeChange('completed', e)}
                disabled={isLoading}
                class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 bg-white [&::-webkit-calendar-picker-indicator]:hidden {validationErrors.completed ? 'border-red-500' : ''}"
            />
              <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-auto">
                <button
                  type="button"
                  on:click={() => document.getElementById('completed-date').showPicker()}
                  class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                >
                  <FontAwesomeIcon icon={faCalendar} />
                </button>
              </div>
            </div>
            {#if validationErrors.completed}
              <div class="text-red-500 text-xs mt-1">{validationErrors.completed}</div>
            {/if}
          </div>
        </div>

        {#if isAdmin && !isMeasurementActivity}
          <div class="form-group">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Settings</label>

            <div class="flex flex-row justify gap-4">
              <div class="flex items-center">
                <span class="text-sm text-gray-700 dark:text-gray-300">CGM Blinded: {cgmBlinded ? 'Yes' : 'No'}</span>
              </div>

              <div class="flex items-center">
                <span class="text-sm text-gray-700 dark:text-gray-300">SMBG Blinded: {smbgBlinded ? 'Yes' : 'No'}</span>
              </div>

              <div class="flex items-center">
                <span class="text-sm text-gray-700 dark:text-gray-300">Enable Alerts: {enableAlerts ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        {/if}

        <div class="flex justify-end space-x-2 pt-6">
          <button
            type="button"
            on:click={handleClose}
            disabled={isLoading}
            class="bg-gray-500 text-white py-1 px-2 rounded hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={$userReadOnly || isLoading || !hasChanges || Object.values(validationErrors).some(error => error)}
            class="py-1 px-2 rounded {$userReadOnly || !hasChanges || Object.values(validationErrors).some(error => error) ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-700'}"
          >
            {isLoading ? 'Saving...' : hasChanges ? 'Save' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
